/**
 * Cross-app UI store - shared with FOCTTA Core via localStorage.
 *
 * KEY DESIGN DECISION (2026-05-17):
 * Core's `apps/web/src/stores/ui.ts` uses Zustand `persist` middleware
 * writing to localStorage under the key `vaultconsent-ui`. Discovery
 * uses the SAME key + SAME shape so that when both apps run at the same
 * origin (`<slug>.foctta.com`), the BU selection automatically
 * synchronises across the two apps without webhooks, URL params, or JWT
 * round-trips.
 *
 * Pick Credit Card BU in Core's TopBar → write to `vaultconsent-ui` →
 * Discovery's mount reads the same key → both apps display the same
 * selection. The browser's `storage` event handles live cross-tab
 * updates when one app writes and the other is mounted in another tab.
 *
 * Edge cases handled:
 *   - 0 BUs on tenant: selection stays null; consumers use tenant-level
 *     defaults (industry-pack resolver already falls back).
 *   - 1 BU: selection is implicit; TopBar renders a label, not a picker.
 *   - Many BUs: TopBar renders the dropdown with "All Business Units"
 *     as the default.
 *   - Standalone Discovery (no Core): Discovery owns the key entirely.
 *     Same code path; no special case needed.
 *
 * Don't rename the key without coordinating with Core - drift breaks
 * the sync.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface UiState {
  /** Currently-selected Business Unit. `null` means "All Business Units". */
  selectedBusinessUnitId: string | null;
  /** Display name of the selected BU. Kept in store so the TopBar can
   *  render the label without a round-trip to the API on every render. */
  selectedBusinessUnitName: string | null;
  /** Sidebar collapsed state - bonus shared bit with Core for the same
   *  reason (cross-app UX consistency at the same origin). */
  sidebarCollapsed: boolean;
  setBusinessUnit: (id: string | null, name: string | null) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      selectedBusinessUnitId: null,
      selectedBusinessUnitName: null,
      sidebarCollapsed: false,
      setBusinessUnit: (id, name) =>
        set({ selectedBusinessUnitId: id, selectedBusinessUnitName: name }),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      toggleSidebar: () =>
        set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
    }),
    {
      // SAME key as Core. Do not rename without cross-app coordination.
      name: 'vaultconsent-ui',
      partialize: (state) => ({
        selectedBusinessUnitId: state.selectedBusinessUnitId,
        selectedBusinessUnitName: state.selectedBusinessUnitName,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    },
  ),
);

/**
 * Listener for cross-tab storage events. Call this once at app mount
 * (e.g. from main.tsx) so when Core (in another tab) writes to
 * `vaultconsent-ui`, Discovery's store rehydrates from the new value
 * without a page refresh.
 *
 * Zustand's persist middleware doesn't subscribe to `storage` events by
 * default - it only reads on mount. This bridge adds the live sync.
 */
export function installCrossTabSync(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = (e: StorageEvent): void => {
    if (e.key !== 'vaultconsent-ui' || e.newValue === e.oldValue) return;
    try {
      const parsed = JSON.parse(e.newValue ?? '{}') as {
        state?: Partial<UiState>;
      };
      const next = parsed.state;
      if (!next) return;
      // Only update fields we share; ignore anything Core writes that
      // we don't recognise.
      useUiStore.setState({
        selectedBusinessUnitId: next.selectedBusinessUnitId ?? null,
        selectedBusinessUnitName: next.selectedBusinessUnitName ?? null,
        sidebarCollapsed: next.sidebarCollapsed ?? false,
      });
    } catch {
      // Malformed payload - ignore. Local state stays as-is.
    }
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
